import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, Users, CalendarDays, Wallet, Loader2 } from "lucide-react";

const fmt0 = (n) => new Intl.NumberFormat("en-NG").format(Math.round(Number(n) || 0));
const fmt2 = (n) => "NGN " + new Intl.NumberFormat("en-NG", { maximumFractionDigits: 2 }).format(Number(n) || 0);

/**
 * HR analytics widget — three compact charts. Renders inline on the admin
 * dashboard for HR/Admin/Disbursement roles.
 */
export default function HrAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [months, setMonths] = useState(6);

    useEffect(() => {
        setLoading(true);
        api.get(`/hr-analytics?months=${months}`)
            .then(({ data }) => setData(data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [months]);

    if (loading) {
        return (
            <div className="bg-white border border-ink/10 p-8 flex items-center gap-3 text-ink/60 text-sm" data-testid="hr-analytics-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Crunching HR analytics…
            </div>
        );
    }
    if (!data) {
        return (
            <div className="bg-white border border-ink/10 p-8 text-ink/50 text-sm" data-testid="hr-analytics-empty">
                Analytics unavailable. Check your permissions.
            </div>
        );
    }

    const trend = data.headcount_trend || [];
    const leaveByPeriod = data.leave_utilisation?.by_period || [];
    const payeByPeriod = data.paye_summary?.by_period || [];
    const maxHeadcount = Math.max(1, ...trend.map((t) => t.count));
    const maxLeave = Math.max(1, ...leaveByPeriod.map((l) => l.days));
    const maxPaye = Math.max(1, ...payeByPeriod.map((p) => p.paye));
    const change = data.headcount_change || 0;
    const Trend = change >= 0 ? TrendingUp : TrendingDown;

    return (
        <div className="bg-white border border-ink/10 p-6 sm:p-8" data-testid="hr-analytics-widget">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="overline">HR analytics{data.dept_scoped ? ` · ${data.department}` : ""}</div>
                    <h2 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink">
                        {data.dept_scoped ? "Department pulse" : "Workforce pulse"} · last {months} months
                    </h2>
                </div>
                <select
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    data-testid="hr-analytics-range"
                    className="border border-ink/15 px-3 py-1.5 text-xs bg-white"
                >
                    {[3, 6, 12, 24].map((n) => (
                        <option key={n} value={n}>{`Last ${n} months`}</option>
                    ))}
                </select>
            </div>

            <div className={`grid grid-cols-1 ${data.dept_scoped ? "lg:grid-cols-2" : "lg:grid-cols-3"} gap-px bg-ink/10 border border-ink/10`}>
                {/* Headcount card */}
                <Card
                    icon={Users}
                    title="Headcount"
                    primary={fmt0(data.current_headcount)}
                    sub={
                        <span className={`inline-flex items-center gap-1 ${change >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            <Trend className="w-3 h-3" />
                            {change >= 0 ? "+" : ""}{fmt0(change)} since {trend[0]?.label || ""}
                        </span>
                    }
                    testid="hr-analytics-headcount"
                >
                    <SparkBars
                        items={trend.map((t) => ({ label: t.label, value: t.count }))}
                        max={maxHeadcount}
                        format={fmt0}
                    />
                </Card>

                {/* Leave card */}
                <Card
                    icon={CalendarDays}
                    title="Leave utilisation"
                    primary={`${fmt0(data.leave_utilisation?.total_days || 0)} days`}
                    sub={`${fmt0(data.leave_utilisation?.employees_with_leave || 0)} employees on leave`}
                    testid="hr-analytics-leave"
                >
                    <SparkBars
                        items={leaveByPeriod.map((l) => ({ label: l.label, value: l.days }))}
                        max={maxLeave}
                        format={(v) => `${fmt0(v)}d`}
                        color="bg-amber-500"
                    />
                    {data.leave_utilisation?.by_type?.length > 0 && (
                        <ul className="mt-3 space-y-0.5 text-xs text-ink/60">
                            {data.leave_utilisation.by_type.slice(0, 3).map((t) => (
                                <li key={t.type} className="flex justify-between">
                                    <span className="truncate">{t.type}</span>
                                    <span className="tabular-nums">{fmt0(t.days)}d</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                {/* PAYE card — hidden for dept_lead (no payroll visibility) */}
                {!data.dept_scoped && (
                    <Card
                        icon={Wallet}
                        title="PAYE collected"
                        primary={fmt2(data.paye_summary?.total || 0)}
                        sub={`Avg ${fmt2(data.paye_summary?.average_per_payslip || 0)} · ${fmt0(data.paye_summary?.payslip_count || 0)} payslips`}
                        testid="hr-analytics-paye"
                    >
                        <SparkBars
                            items={payeByPeriod.map((p) => ({ label: p.label, value: p.paye }))}
                            max={maxPaye}
                            format={(v) => fmt2(v).replace("NGN ", "")}
                            color="bg-emerald-600"
                        />
                    </Card>
                )}
            </div>

            {/* Department breakdown */}
            {data.department_breakdown?.length > 0 && (
                <div className="mt-6">
                    <div className="overline mb-2">Headcount by department</div>
                    <div className="space-y-1.5" data-testid="hr-analytics-departments">
                        {data.department_breakdown.slice(0, 8).map((d) => {
                            const pct = data.current_headcount > 0 ? Math.round((d.count / data.current_headcount) * 100) : 0;
                            return (
                                <div key={d.department} className="flex items-center gap-3">
                                    <div className="text-xs text-ink/70 truncate flex-1 min-w-0">{d.department}</div>
                                    <div className="flex-1 max-w-md bg-ink/5 h-2">
                                        <div className="h-2 bg-klein" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="text-xs tabular-nums text-ink/60 w-16 text-right">
                                        {fmt0(d.count)} · {pct}%
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function Card({ icon: Icon, title, primary, sub, children, testid }) {
    return (
        <div className="bg-white p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-ink/60">
                <Icon className="w-4 h-4" />
                <span className="overline">{title}</span>
            </div>
            <div className="mt-2 font-display font-black text-2xl tracking-tight text-ink tabular-nums truncate">
                {primary}
            </div>
            <div className="text-xs text-ink/60 mt-0.5">{sub}</div>
            <div className="mt-4">{children}</div>
        </div>
    );
}

function SparkBars({ items, max, format, color = "bg-klein" }) {
    if (!items?.length) return <div className="text-xs text-ink/40">No data.</div>;
    return (
        <div className="flex items-end gap-1 h-16">
            {items.map((it, i) => {
                const h = max > 0 ? Math.max(2, Math.round((it.value / max) * 56)) : 2;
                return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                        <div className={`w-full ${color} transition-all`} style={{ height: `${h}px` }} />
                        <div className="text-[9px] text-ink/40 tabular-nums truncate w-full text-center">
                            {it.label?.split(" ")[0]?.slice(0, 3) || ""}
                        </div>
                        <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-ink text-white text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                            {it.label}: {format ? format(it.value) : it.value}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
